/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {NewDatatable} from '../../../../components/NewDataTable/NewDataTable'
import {Button} from '../../../../components/Button/Button'
import {Loading} from '../../../../components/Loading/Loading'
import {POST} from '../../../../services/api'
import {i18n} from '../../../../translate/i18n'
import {Input} from '../../../../components/Input/Input'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import useModals from '../../../../hooks/useModals'
import {ModalWrapper} from '../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import Swal from 'sweetalert2'

type MetaType = {
  itemCount: number
  anonymousReviewsCount: number
  namedReviewsCount: number
}

type FeedbackFormType = {
  id: number
  pluginspaceId: number
  status: boolean
  created: string
  updated: string
  title: string
  description: string
  content: {
    config: Array<{
      order: number
      type: string
      settings: {
        customTitle?: string
        gridHotelFeedbackIds?: number[]
      }
    }>
  }
  dateStart: string
  dateEnd: string | null
}

type CustomerType = {
  id: number
  name: string
  email: string
  doc: string
  cpf: string
  birthDate: string
  gender: string
  status: string
}

type HotelReservationType = {
  id: number
  pluginspaceId: number
  roomId: number
  customerId: number
  checkIn: string
  checkOut: string | null
  status: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  customer: CustomerType
}

type FeedbackContentType = {
  order: number
  type: string
  label: string | null
  value: any
}

type FeedbackReviewType = {
  id: number
  pluginspaceId: number
  anonymous: boolean
  reservationId: number
  feedBackFormId: number
  content: FeedbackContentType[]
  created: string
  updated: string
  hotelReservation: HotelReservationType
  feedBackForm: FeedbackFormType
  // Campos processados
  dateFormatted?: string
  timeFormatted?: string
  statusLabel?: string
  ratingDisplay?: string
  feedbackFormName?: string
  customerDisplay?: string
  roomDisplay?: string
  reservationDisplay?: string
}

interface SearchFeedbackReviewDto {
  feedBackFormId?: number
  reservationId?: number
  status?: string
  createdFrom?: string
  createdTo?: string
  rating?: number
  anonymous?: string
}

const FeedbackReviewsContent: FC = () => {
  const {showWrapperModal, closeWrapperModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [reviews, setReviews] = useState<FeedbackReviewType[]>([])
  const [, setMeta] = useState<MetaType | undefined>(undefined)
  const [filters, setFilters] = useState<SearchFeedbackReviewDto>({})
  const [selectedReview, setSelectedReview] = useState<FeedbackReviewType | null>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [feedbackForms, setFeedbackForms] = useState<
    Array<{value: number | string; select: string}>
  >([])

  const getReviews = async (
    searchFilters?: SearchFeedbackReviewDto,
    page: number = 1,
    take: number = 100,
    search: string = ''
  ) => {
    try {
      setLoading(true)

      const filtersToUse = searchFilters || filters

      // Constrói a URL com os parâmetros de paginação
      const queryParams = new URLSearchParams({
        page: page.toString(),
        take: take.toString(),
        ...(search && {search}),
      })

      const res = await POST(`/feedback-review-hotel/filter?${queryParams}`, filtersToUse)

      if (res.success && res.dados) {
        const processedData = res.dados.map((item: FeedbackReviewType) => {
          // Calcular avaliação média baseada nas estrelas do conteúdo
          const starResponses = item.content.filter((c) => c.type === 'stars' && c.value !== null)
          const averageRating =
            starResponses.length > 0
              ? starResponses.reduce((sum, c) => sum + (c.value || 0), 0) / starResponses.length
              : 0

          return {
            ...item,
            dateFormatted: new Date(item.created).toLocaleDateString('pt-BR'),
            timeFormatted: new Date(item.created).toLocaleTimeString('pt-BR'),
            statusLabel: i18n.t('feedback_reviews.status_pending'), // Como não temos status no retorno, definir padrão
            ratingDisplay: generateStarsDisplay(Math.round(averageRating)),
            feedbackFormName: item.feedBackForm?.title || '-',
            customerDisplay: item.anonymous
              ? i18n.t('feedback_reviews.anonymous_label')
              : item.hotelReservation?.customer?.name ||
                i18n.t('feedback_reviews.client_not_identified'),
            roomDisplay: item.hotelReservation?.roomId
              ? `Quarto ${item.hotelReservation.roomId}`
              : '-',
            reservationDisplay: item.anonymous ? '-' : item.hotelReservation?.id || '-',
          }
        })
        setReviews(processedData)
        setMeta(res.meta)
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('feedback_reviews.error') as string,
        text: i18n.t('feedback_reviews.error_loading_reviews') as string,
      })
    }
  }

  const getFeedbackForms = async () => {
    try {
      const res = await POST('/feedbackform/filter', {})
      if (res.success && res.dados) {
        const formsOptions = res.dados.map((form: any) => ({
          value: form.id,
          select: form.title,
        }))
        formsOptions.unshift({
          value: '',
          select: i18n.t('feedback_reviews.filters.all_forms'),
        })
        setFeedbackForms(formsOptions)
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleFilterChange = (field: keyof SearchFeedbackReviewDto, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value === 0 || value === '' ? undefined : value,
    }))
  }

  const handleSearch = () => {
    getReviews(filters)
  }

  const handleClearFilters = () => {
    setFilters({})
    getReviews({})
  }

  const generateStarsDisplay = (rating: number) => {
    const stars = []
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push('★')
      } else {
        stars.push('☆')
      }
    }
    return stars.join('')
  }

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-success'
    if (rating >= 3) return 'text-warning'
    return 'text-danger'
  }

  // Função para buscar detalhes do review
  const getReviewDetails = async (reviewId: number) => {
    try {
      setLoadingDetails(true)
      showWrapperModal(i18n.t('feedback_reviews.review_details') as string)

      // Como não temos endpoint específico, vamos usar os dados que já temos
      const review = reviews.find((r) => r.id === reviewId)
      if (review) {
        setSelectedReview(review)
      }
      setLoadingDetails(false)
    } catch (e) {
      setLoadingDetails(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('feedback_reviews.error') as string,
        text: i18n.t('feedback_reviews.error_loading_details') as string,
      })
    }
  }

  useEffect(() => {
    Promise.all([getReviews(), getFeedbackForms()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('permissions.feedback')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('feedback_reviews.subtitle')}
                </span>
              </h3>
            </div>

            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    label={i18n.t('feedback_reviews.filters.feedback_form')}
                    inputAttr={{
                      type: 'select',
                      placeholder: i18n.t('feedback_reviews.filters.select_form_placeholder'),
                    }}
                    value={filters.feedBackFormId || ''}
                    change={(value: number) => handleFilterChange('feedBackFormId', value)}
                    options={feedbackForms}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('feedback_reviews.filters.anonymous')}
                    inputAttr={{
                      type: 'select',
                      placeholder: i18n.t('feedback_reviews.filters.select_anonymous_placeholder'),
                    }}
                    value={filters.anonymous || ''}
                    change={(value: string) => handleFilterChange('anonymous', value)}
                    options={[
                      {
                        value: '',
                        select: i18n.t('feedback_reviews.filters.select_anonymous_placeholder'),
                      },
                      {value: 'true', select: i18n.t('feedback_reviews.anonymous_active')},
                      {value: 'false', select: i18n.t('feedback_reviews.anonymous_inactive')},
                    ]}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('feedback_reviews.filters.start_date')}
                    inputAttr={{
                      type: 'date',
                      placeholder: i18n.t('feedback_reviews.filters.start_date_placeholder'),
                    }}
                    value={filters.createdFrom || ''}
                    change={(value: string) =>
                      handleFilterChange('createdFrom', value || undefined)
                    }
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('feedback_reviews.filters.end_date')}
                    inputAttr={{
                      type: 'date',
                      placeholder: i18n.t('feedback_reviews.filters.end_date_placeholder'),
                    }}
                    value={filters.createdTo || ''}
                    change={(value: string) => handleFilterChange('createdTo', value || undefined)}
                  />
                </div>

                <div className='col-12 d-flex justify-content-end'>
                  <Button
                    text={i18n.t('feedback_reviews.filters.clear_filters')}
                    color='danger'
                    iconColor='#6c757d'
                    size='small'
                    horizontalPadding={4}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={handleClearFilters}
                    btnClass='me-2'
                  />
                  <Button
                    text={i18n.t('feedback_reviews.filters.filter_button')}
                    color='primary'
                    iconColor='#FFF'
                    icon='fas fa-filter'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={handleSearch}
                    btnClass='mb-0 fw-bolder'
                  />
                </div>
              </div>
            </div>

            <div className='card-body'>
              <NewDatatable
                data={reviews}
                headers={[
                  {
                    key: i18n.t('feedback_reviews.table.feedback_form'),
                    value: 'feedbackFormName',
                    type: 'default',
                  },
                  {
                    key: i18n.t('feedback_reviews.table.customer'),
                    value: 'customerDisplay',
                    type: 'default',
                  },
                  {
                    key: i18n.t('feedback_reviews.table.reservation'),
                    value: 'reservationDisplay',
                    type: 'default',
                  },
                  {
                    key: i18n.t('feedback_reviews.filters.date'),
                    value: 'dateFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('feedback_reviews.table.actions'),
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-eye',
                    action: (item: any) => {
                      getReviewDetails(item.id)
                    },
                    title: i18n.t('feedback_reviews.actions.view_details'),
                  },
                ]}
                color='primary'
                onChangeData={async (take: number, page: number, search: string) => {
                  await getReviews(filters, page, take, search)
                }}
                search
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal para visualizar detalhes do review */}
      {selectedReview && (
        <ModalWrapper
          confirmAction={() => {
            closeWrapperModal()
            setSelectedReview(null)
          }}
          textAction={i18n.t('feedback_reviews.close')}
          noCloseButton={true}
          confirmButtomColor='danger'
          maxWidth={true}
          isFooterFixed={true}
        >
          {loadingDetails ? (
            <div className='text-center p-5'>
              <div
                className='spinner-border text-primary'
                role='status'
                style={{width: '3rem', height: '3rem'}}
              >
                <span className='sr-only'>{i18n.t('feedback_reviews.loading')}</span>
              </div>
              <p className='mt-3 text-muted'>{i18n.t('feedback_reviews.loading_details')}</p>
            </div>
          ) : (
            <div className='p-0'>
              {/* Header do Review */}
              <div className='p-4 my-4 border border-gray-300 rounded'>
                <div className='d-flex align-items-center justify-content-between flex-wrap'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-50px me-3'>
                      <div className='symbol-label bg-light-primary'>
                        <i
                          className={`fas ${
                            selectedReview.anonymous ? 'fa-user-secret' : 'fa-user-check'
                          } text-primary fs-2`}
                        ></i>
                      </div>
                    </div>
                    <div>
                      <h4 className='fw-bold mb-1'>
                        {selectedReview.anonymous
                          ? i18n.t('feedback_reviews.anonymous_label')
                          : selectedReview.hotelReservation?.customer?.name ||
                            i18n.t('feedback_reviews.client_not_identified')}
                      </h4>
                      <p className='text-muted mb-0'>
                        {selectedReview.anonymous
                          ? i18n.t('feedback_reviews.email_anonymous')
                          : selectedReview.hotelReservation?.customer?.email ||
                            i18n.t('feedback_reviews.email_not_informed')}
                      </p>
                    </div>
                  </div>
                  {/* <div className='text-end'>
                    <div className='d-flex align-items-center mb-2'>
                      <span className='text-primary fw-bold fs-4 me-2'>
                        {selectedReview.ratingDisplay || '-'}
                      </span>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Informações da Reserva - só mostra se tiver dados de reserva */}
              <div className='p-4'>
                {selectedReview.hotelReservation || selectedReview.reservationId ? (
                  <div className='row g-3 mb-5'>
                    <div className='col-md-4'>
                      <div className='card border-0 shadow-sm h-100'>
                        <div className='card-body text-center'>
                          <i className='fas fa-bed text-primary fs-2 mb-3'></i>
                          <h6 className='fw-bold'>{i18n.t('feedback_reviews.room')}</h6>
                          <p className='mb-0'>
                            {selectedReview.anonymous
                              ? i18n.t('feedback_reviews.anonymous_label')
                              : selectedReview.roomDisplay}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-4'>
                      <div className='card border-0 shadow-sm h-100'>
                        <div className='card-body text-center'>
                          <i className='fas fa-calendar text-primary fs-2 mb-3'></i>
                          <h6 className='fw-bold'>{i18n.t('feedback_reviews.reservation')}</h6>
                          <p className='mb-0'>
                            {selectedReview.anonymous
                              ? i18n.t('feedback_reviews.anonymous_label')
                              : selectedReview.reservationId || '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-4'>
                      <div className='card border-0 shadow-sm h-100'>
                        <div className='card-body text-center'>
                          <i className='fas fa-clipboard-list text-primary fs-2 mb-3'></i>
                          <h6 className='fw-bold'>{i18n.t('feedback_reviews.feedback_form')}</h6>
                          <p className='mb-0'>{selectedReview.feedBackForm?.title || '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='alert alert-info mb-5'>
                    <div className='d-flex align-items-center'>
                      <i className='fas fa-info-circle me-2'></i>
                      <div>
                        <strong>{i18n.t('feedback_reviews.form_label')}</strong>
                        {selectedReview.feedBackForm?.title || '-'}
                        <br />
                        <small className='text-muted'>
                          Esta resposta não está vinculada a uma reserva específica
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {/* Respostas do Feedback */}
                {selectedReview.content && selectedReview.content.length > 0 ? (
                  <div className='card border-0 shadow-sm mb-5'>
                    <div className='card-header'>
                      <h5 className='card-title mb-0'>
                        <i className='fas fa-comments me-2'></i>
                        {i18n.t('feedback_reviews.responses')}
                      </h5>
                    </div>
                    <div className='card-body'>
                      {selectedReview.content
                        .filter(
                          (response: FeedbackContentType) =>
                            response.type !== 'title' ||
                            (response.type === 'title' && response.label)
                        )
                        .map((response: FeedbackContentType, index: number) => {
                          const typeLabel =
                            (i18n.t(`feedback_reviews.types.${response.type}`) as string) ||
                            response.type
                          return (
                            <div key={index} className='mb-4 p-3 bg-light rounded'>
                              <div className='d-flex align-items-center mb-2'>
                                <span className='badge bg-primary me-2'>{typeLabel}</span>
                                {response.label && <strong>{response.label}</strong>}
                                {response.type === 'title' && !response.label && (
                                  <strong>{i18n.t('feedback_reviews.response_title')}</strong>
                                )}
                              </div>
                              <div className='ms-3'>
                                {response.type === 'title' ? (
                                  <p className='mb-0 fw-bold text-primary'>
                                    {/* {response.label || 'Título do formulário'} */}
                                  </p>
                                ) : response.type === 'stars' ? (
                                  response.value !== null ? (
                                    <span
                                      className={`${getRatingColor(response.value || 0)} fw-bold`}
                                    >
                                      {generateStarsDisplay(response.value || 0)} (
                                      {response.value || 0}/5)
                                    </span>
                                  ) : (
                                    <span className='text-muted'>
                                      {i18n.t('feedback_reviews.no_rating_provided')}
                                    </span>
                                  )
                                ) : response.type === 'checkbox' ? (
                                  response.value &&
                                  Array.isArray(response.value) &&
                                  response.value.length > 0 ? (
                                    <ul className='mb-0'>
                                      {response.value.map((item: string, idx: number) => (
                                        <li key={idx}>{item}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span className='text-muted'>
                                      {i18n.t('feedback_reviews.no_items_selected')}
                                    </span>
                                  )
                                ) : response.type === 'radio' || response.type === 'select' ? (
                                  <span className='fw-bold text-primary'>
                                    {response.value ||
                                      i18n.t('feedback_reviews.no_option_selected')}
                                  </span>
                                ) : (
                                  <p className='mb-0'>
                                    {response.value ||
                                      i18n.t('feedback_reviews.no_answer_provided')}
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  </div>
                ) : (
                  <div className='alert alert-warning mb-5'>
                    <div className='d-flex align-items-center'>
                      <i className='fas fa-exclamation-triangle me-2'></i>
                      <span>{i18n.t('feedback_reviews.no_detailed_responses')}</span>
                    </div>
                  </div>
                )}

                {/* Informações Técnicas */}
                <div className='card border-0 shadow-sm mb-4'>
                  <div className='card-header'>
                    <h6 className='card-title mb-0'>
                      <i className='fas fa-info-circle me-2'></i>
                      {i18n.t('feedback_reviews.technical_info')}
                    </h6>
                  </div>
                  <div className='card-body'>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>
                          {i18n.t('feedback_reviews.created_at')}
                        </small>
                        <span>{new Date(selectedReview.created).toLocaleString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalWrapper>
      )}
    </>
  )
}

const FeedbackReviews: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {
            title: i18n.t('feedback_reviews.breadcrumb_feedback'),
            path: 'feedback-satisfaction',
            isActive: false,
          },
          {
            title: i18n.t('permissions.feedback'),
            path: 'feedback-reviews',
            isActive: true,
          },
        ]}
      >
        {i18n.t('permissions.feedback')}
      </PageTitle>
      <FeedbackReviewsContent />
    </>
  )
}

export {FeedbackReviews}
